import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import type { StoreProfileFormData } from '@/lib/validators';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface StoreLocationFormProps {
  register: UseFormRegister<StoreProfileFormData>;
  errors: FieldErrors<StoreProfileFormData>;
  setValue: UseFormSetValue<StoreProfileFormData>;
  watch: UseFormWatch<StoreProfileFormData>;
}

const BAKU_MALLS = [
  { value: 'ganjlik', label: 'Ganjlik Mall' },
  { value: '28mall', label: '28 Mall' },
  { value: 'port-baku', label: 'Port Baku Mall' },
  { value: 'park-bulvar', label: 'Park Bulvar' },
  { value: 'deniz-mall', label: 'Deniz Mall' },
];

const BAKU_CENTER: [number, number] = [40.4093, 49.8671];

function DraggableMarker({ setValue }: { setValue: UseFormSetValue<StoreProfileFormData> }) {
  useMapEvents({
    click(e) {
      setValue('latitude', e.latlng.lat);
      setValue('longitude', e.latlng.lng);
    },
  });

  return null;
}

export function StoreLocationForm({ register, errors, setValue, watch }: StoreLocationFormProps) {
  const latitude = watch('latitude') || BAKU_CENTER[0];
  const longitude = watch('longitude') || BAKU_CENTER[1];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Location</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <Select
            label="Mall"
            {...register('mall')}
            error={errors.mall?.message}
            options={[{ value: '', label: 'Select a mall' }, ...BAKU_MALLS]}
          />
          <Input label="Floor" {...register('floor')} error={errors.floor?.message} placeholder="1" />
          <Input
            label="Unit"
            {...register('unit')}
            error={errors.unit?.message}
            placeholder="A-15"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Pin your exact location
          </label>
          <div className="h-[400px] overflow-hidden rounded-lg border">
            <MapContainer center={BAKU_CENTER} zoom={16} className="h-full w-full">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              <Marker position={[latitude, longitude]} draggable={true} />
              <DraggableMarker setValue={setValue} />
            </MapContainer>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Click on the map to set your store's location. Coordinates: {latitude.toFixed(6)},{' '}
            {longitude.toFixed(6)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
